import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/business-life-insurance-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

/* ─── Business Coverage Needs Analyzer ──────────────────────────────────── */

type BusinessType = 'sole-proprietor' | 'partnership' | 'llc' | 'corporation' | 'professional';
type BusinessNeed = 'key-person' | 'buy-sell' | 'sba-loan' | 'executive-benefit' | 'group-life';

interface BusinessCoverageResult {
  need: string;
  product: string;
  coverageRange: string;
  carrier: string;
  monthlyEst: string;
  priority: 'critical' | 'recommended' | 'optional';
  description: string;
}

function analyzeBusinessNeeds(
  type: BusinessType,
  needs: BusinessNeed[],
  revenue: number,
  employees: number
): BusinessCoverageResult[] {
  const results: BusinessCoverageResult[] = [];

  if (needs.includes('key-person')) {
    const coverage = Math.min(revenue * 2, 5000000);
    const fmtCov = coverage >= 1000000 ? `$${coverage / 1000000}M` : `$${coverage / 1000}K`;
    results.push({
      need: 'Key Person Insurance',
      product: 'Term Life — Key Person',
      coverageRange: `${fmtCov} (2× annual revenue)`,
      carrier: 'Pacific Life / Banner Life',
      monthlyEst: `$${Math.round(coverage / 1000000 * 45 + 30)}/mo`,
      priority: 'critical',
      description: 'Protects your business if a key owner, partner, or employee dies. Pays the business directly to cover lost revenue, recruitment costs, and operational disruption.',
    });
  }

  if (needs.includes('buy-sell') && (type === 'partnership' || type === 'llc' || type === 'corporation')) {
    const coverage = Math.min(revenue * 1.5, 3000000);
    const fmtCov = coverage >= 1000000 ? `$${coverage / 1000000}M` : `$${coverage / 1000}K`;
    results.push({
      need: 'Buy-Sell Agreement Funding',
      product: 'Whole Life or Term — Cross-Purchase',
      coverageRange: `${fmtCov} (1.5× annual revenue)`,
      carrier: 'Mutual of Omaha / North American',
      monthlyEst: `$${Math.round(coverage / 1000000 * 60 + 40)}/mo`,
      priority: 'critical',
      description: 'Funds your buy-sell agreement so surviving partners can purchase a deceased partner\'s share at fair market value — without liquidating the business or taking on debt.',
    });
  }

  if (needs.includes('sba-loan')) {
    results.push({
      need: 'SBA Loan Collateral Assignment',
      product: 'Term Life — Collateral Assignment',
      coverageRange: 'Equal to outstanding SBA loan balance',
      carrier: 'Protective Life / Banner Life',
      monthlyEst: '$28–$85/mo',
      priority: 'critical',
      description: 'SBA lenders require life insurance as collateral for loans over $350,000. A collateral assignment policy satisfies this requirement and protects your personal assets.',
    });
  }

  if (needs.includes('executive-benefit')) {
    results.push({
      need: 'Executive Benefit / COLI',
      product: 'Indexed Universal Life (IUL)',
      coverageRange: '$500K–$5M per executive',
      carrier: 'North American / Nationwide',
      monthlyEst: '$200–$800/mo per executive',
      priority: 'recommended',
      description: 'Company-Owned Life Insurance (COLI) funds executive compensation, deferred comp plans, and golden handcuffs. Tax-advantaged cash value growth with market-linked returns.',
    });
  }

  if (needs.includes('group-life') && employees >= 2) {
    results.push({
      need: 'Group Life Insurance',
      product: 'Group Term Life',
      coverageRange: `$${employees * 50}K–$${employees * 100}K total`,
      carrier: 'Principal / Guardian / MetLife',
      monthlyEst: `$${employees * 8}–$${employees * 15}/mo`,
      priority: employees >= 5 ? 'recommended' : 'optional',
      description: 'Employer-paid group life insurance is a low-cost employee benefit that attracts and retains talent. Premiums are tax-deductible as a business expense.',
    });
  }

  if (type === 'sole-proprietor' && results.length === 0) {
    results.push({
      need: 'Personal Business Protection',
      product: 'Term Life — Personal Policy',
      coverageRange: '$250K–$1M',
      carrier: 'Banner Life / Protective Life',
      monthlyEst: '$18–$65/mo',
      priority: 'critical',
      description: 'As a sole proprietor, your business and personal finances are inseparable. A personal term life policy protects your family from business debts and lost income if you die.',
    });
  }

  return results;
}

/* ─── FAQs ───────────────────────────────────────────────────────────────── */

const FAQS = [
  {
    q: 'What is key person life insurance for Indianapolis businesses?',
    a: 'Key person life insurance (also called key man insurance) is a life insurance policy owned by a business on a key employee or owner whose death would significantly impact the company\'s revenue or operations. The business pays the premiums and is the beneficiary. If the key person dies, the death benefit is paid to the business — not the family — to cover lost revenue, recruitment costs, and operational disruption. In Indianapolis, key person coverage typically equals 2–5× the key person\'s annual compensation or 1–2× annual business revenue.',
  },
  {
    q: 'How does a buy-sell agreement work with life insurance in Indianapolis?',
    a: 'A buy-sell agreement is a legally binding contract between business partners that determines what happens to each partner\'s ownership share if they die, become disabled, or want to exit the business. Life insurance funds the buy-sell agreement: each partner takes out a policy on the other partner(s). If one partner dies, the surviving partner(s) use the death benefit to purchase the deceased partner\'s share at a pre-agreed price. This prevents the deceased partner\'s family from becoming unwanted business partners and ensures the surviving partners maintain control. In Indianapolis, buy-sell agreements are most common for partnerships, LLCs, and closely-held corporations.',
  },
  {
    q: 'Does the SBA require life insurance for business loans in Indianapolis?',
    a: 'Yes. The SBA requires life insurance as collateral for most SBA 7(a) loans over $350,000. The policy must be assigned to the lender as collateral — this is called a collateral assignment. The coverage amount must equal the outstanding loan balance. As the loan is paid down, the required coverage decreases. Indianapolis business owners typically use term life insurance for SBA loan collateral because it\'s the most cost-effective option. The business or the owner can own the policy, but the lender must be named as collateral assignee.',
  },
  {
    q: 'How much does business life insurance cost in Indianapolis?',
    a: 'Business life insurance costs in Indianapolis vary significantly by type: Key person term life insurance: $30–$150/month for $500K–$2M of coverage, depending on the insured\'s age and health. Buy-sell agreement funding: $40–$200/month per partner for $500K–$3M of coverage. SBA loan collateral: $28–$85/month for $250K–$1M of coverage. Group life insurance: $8–$15/month per employee for $50K of coverage. Executive benefit (IUL): $200–$800/month per executive. As an independent agent, we compare 20+ carriers to find the lowest rate for your specific business situation.',
  },
  {
    q: 'What is the difference between key person insurance and a buy-sell agreement?',
    a: 'Key person insurance and buy-sell agreement funding are two different types of business life insurance that serve different purposes. Key person insurance: The business is the owner and beneficiary. The death benefit is paid to the business to cover operational losses, recruitment costs, and revenue disruption caused by the key person\'s death. Buy-sell agreement funding: Each partner owns a policy on the other partner(s). The death benefit is paid to the surviving partner(s) to purchase the deceased partner\'s ownership share. Many Indianapolis businesses need both — key person insurance to protect operations, and buy-sell funding to handle ownership transition.',
  },
  {
    q: 'Can an Indianapolis LLC get life insurance on its members?',
    a: 'Yes. An LLC can own life insurance on its members (owners) for key person protection or buy-sell agreement funding. The LLC pays the premiums and is the beneficiary for key person policies. For buy-sell agreements, the LLC can use an entity purchase (redemption) structure where the LLC owns policies on all members, or a cross-purchase structure where each member owns policies on the other members. The right structure depends on the number of members, tax considerations, and the specific buy-sell agreement terms. We work with Indianapolis business attorneys to ensure the insurance structure aligns with your legal agreements.',
  },
  {
    q: 'Is business life insurance tax-deductible in Indianapolis?',
    a: 'It depends on the type: Key person insurance premiums are generally NOT tax-deductible (the death benefit is also tax-free to the business). Group life insurance premiums ARE tax-deductible as a business expense (up to $50K of coverage per employee). Executive benefit (COLI) premiums are generally not deductible, but the cash value grows tax-deferred and death benefits are tax-free. Buy-sell agreement premiums are generally not deductible. Consult your Indianapolis CPA or tax advisor for guidance specific to your business structure and situation.',
  },
  {
    q: 'How quickly can an Indianapolis business get life insurance coverage?',
    a: 'Business life insurance approval timelines in Indianapolis: Term life (key person, buy-sell, SBA): 24–72 hours with accelerated underwriting for healthy applicants under 60; 3–6 weeks with full underwriting for larger amounts or older applicants. Group life insurance: 1–2 weeks for groups of 2–9 employees; 2–4 weeks for larger groups. Executive benefit (IUL): 3–6 weeks with full underwriting. For SBA loan requirements, we can often get a conditional approval letter within 48 hours to satisfy lender requirements while full underwriting is completed.',
  },
  {
    q: 'What Indianapolis businesses need key person life insurance?',
    a: 'Any Indianapolis business where one or more individuals are critical to revenue generation, client relationships, or operations should have key person insurance. This includes: Professional service firms (law firms, accounting firms, medical practices) where clients follow specific professionals. Technology companies where a founder or lead developer holds critical IP or client relationships. Sales-driven businesses where a top salesperson generates a disproportionate share of revenue. Manufacturing or construction companies where a key operator or estimator is irreplaceable. Family businesses where the founder is the primary revenue driver. If your business would struggle to survive or maintain revenue without a specific person, that person needs key person coverage.',
  },
  {
    q: 'How do I get a business life insurance quote in Indianapolis?',
    a: 'Call Hoosier Life Insurance at (317) 648-2983 or use the Business Coverage Needs Analyzer on this page. We\'ll ask about your business type, number of partners/employees, annual revenue, and specific coverage needs. As an independent agent, we compare 20+ carriers to find the best rate for your business situation. We also work with your business attorney and CPA to ensure the insurance structure aligns with your legal agreements and tax strategy. Most Indianapolis business owners receive their quote within 24 hours.',
  },
];

/* ─── Business Types ─────────────────────────────────────────────────────── */

const BUSINESS_SOLUTIONS = [
  {
    icon: 'ri-user-star-line',
    title: 'Key Person Insurance',
    subtitle: 'Protect your business from losing a critical person',
    desc: 'If a key owner, partner, or employee dies, your business receives a tax-free death benefit to cover lost revenue, recruitment costs, and operational disruption. Coverage typically equals 2–5× the key person\'s annual compensation.',
    coverage: '$250K–$5M',
    carriers: ['Pacific Life', 'Banner Life', 'Protective Life'],
    bestFor: 'Any business with one or more people critical to revenue',
    priority: 'critical',
  },
  {
    icon: 'ri-shake-hands-line',
    title: 'Buy-Sell Agreement Funding',
    subtitle: 'Ensure smooth ownership transition',
    desc: 'Life insurance funds your buy-sell agreement so surviving partners can purchase a deceased partner\'s share at fair market value — without liquidating the business, taking on debt, or dealing with the deceased\'s family as unwanted partners.',
    coverage: '$250K–$10M',
    carriers: ['Mutual of Omaha', 'North American', 'Pacific Life'],
    bestFor: 'Partnerships, LLCs, and closely-held corporations',
    priority: 'critical',
  },
  {
    icon: 'ri-bank-line',
    title: 'SBA Loan Collateral',
    subtitle: 'Satisfy SBA lender requirements',
    desc: 'SBA lenders require life insurance as collateral for loans over $350,000. A collateral assignment policy satisfies this requirement and protects your personal assets from business loan liability.',
    coverage: 'Equal to loan balance',
    carriers: ['Protective Life', 'Banner Life', 'Transamerica'],
    bestFor: 'Indianapolis businesses with SBA 7(a) or 504 loans',
    priority: 'critical',
  },
  {
    icon: 'ri-vip-crown-line',
    title: 'Executive Benefits (COLI)',
    subtitle: 'Attract and retain top talent',
    desc: 'Company-Owned Life Insurance (COLI) funds executive compensation, deferred comp plans, and golden handcuffs. Tax-advantaged cash value growth with market-linked returns through Indexed Universal Life.',
    coverage: '$500K–$5M per executive',
    carriers: ['North American', 'Nationwide', 'Pacific Life'],
    bestFor: 'Indianapolis businesses competing for executive talent',
    priority: 'recommended',
  },
  {
    icon: 'ri-team-line',
    title: 'Group Life Insurance',
    subtitle: 'Employee benefit that attracts talent',
    desc: 'Employer-paid group life insurance is a low-cost employee benefit. Premiums are tax-deductible as a business expense. Employees receive $50K of coverage tax-free. Available for groups of 2+ employees.',
    coverage: '$50K–$500K per employee',
    carriers: ['Principal', 'Guardian', 'MetLife'],
    bestFor: 'Indianapolis businesses with 2+ employees',
    priority: 'recommended',
  },
  {
    icon: 'ri-briefcase-line',
    title: 'Business Overhead Expense',
    subtitle: 'Keep the lights on if you\'re disabled',
    desc: 'Business Overhead Expense (BOE) insurance pays your business\'s fixed monthly expenses — rent, utilities, payroll — if you become disabled and can\'t work. Keeps your Indianapolis business running while you recover.',
    coverage: '$5K–$25K/month',
    carriers: ['Principal', 'Guardian', 'The Standard'],
    bestFor: 'Solo practitioners and small business owners',
    priority: 'recommended',
  },
];

/* ─── Testimonials ───────────────────────────────────────────────────────── */

const TESTIMONIALS = [
  {
    name: 'David & Sarah K.',
    business: 'K&K Architecture, Indianapolis',
    quote: 'We had a buy-sell agreement but no funding. Benjamin set up cross-purchase policies for both of us in 3 weeks. Now if either of us dies, the other can buy out the business without touching personal savings.',
    coverage: '$1.2M buy-sell funding',
    type: 'Buy-Sell Agreement',
  },
  {
    name: 'Marcus T.',
    business: 'Precision Manufacturing, Fishers IN',
    quote: 'Our SBA lender required life insurance as collateral for our $800K expansion loan. Benjamin got us a collateral assignment policy approved in 48 hours. Closed the loan on time.',
    coverage: '$800K SBA collateral',
    type: 'SBA Loan Collateral',
  },
  {
    name: 'Jennifer R.',
    business: 'Riverside Dental Group, Carmel IN',
    quote: 'I\'m the only dentist in my practice. If I died, the practice would collapse. Benjamin set up $2M of key person coverage on me. My staff and patients are protected.',
    coverage: '$2M key person',
    type: 'Key Person Insurance',
  },
];

export default function BusinessLifeInsuranceIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Business Coverage Needs Analyzer
  const [bizType, setBizType] = useState<BusinessType>('llc');
  const [bizNeeds, setBizNeeds] = useState<BusinessNeed[]>(['key-person']);
  const [revenue, setRevenue] = useState(500000);
  const [employees, setEmployees] = useState(5);
  const [analyzerResults, setAnalyzerResults] = useState<BusinessCoverageResult[] | null>(null);

  const toggleNeed = (need: BusinessNeed) => {
    setBizNeeds(prev =>
      prev.includes(need) ? prev.filter(n => n !== need) : [...prev, need]
    );
    setAnalyzerResults(null);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'Business Life Insurance Indianapolis IN | Key Person, Buy-Sell & SBA | Hoosier Life';
    const description = 'Business life insurance in Indianapolis, Indiana. Key person insurance, buy-sell agreement funding, SBA loan collateral, and executive benefits. Compare 20+ carriers. Free business coverage analysis.';
    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: 'business life insurance Indianapolis, key person insurance Indianapolis, buy-sell agreement Indianapolis, SBA loan life insurance Indianapolis Indiana' },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
    ];
    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });
    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          name: title,
          description,
          url: CANONICAL,
          dateModified: '2026-04-15T00:00:00Z',
        },
        {
          '@type': 'Service',
          serviceType: 'Business Life Insurance',
          name: 'Business Life Insurance Indianapolis',
          description: 'Key person insurance, buy-sell agreement funding, SBA loan collateral, and executive benefits for Indianapolis businesses.',
          url: CANONICAL,
          provider: {
            '@type': 'InsuranceAgency',
            name: 'Hoosier Life Insurance',
            telephone: '+1-317-648-2983',
            url: SITE_URL,
            address: { '@type': 'PostalAddress', addressLocality: 'Indianapolis', addressRegion: 'IN', addressCountry: 'US' },
          },
          areaServed: { '@type': 'City', name: 'Indianapolis' },
        },
        {
          '@type': 'FAQPage',
          mainEntity: FAQS.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: 'Business Life Insurance Indianapolis', item: CANONICAL },
          ],
        },
      ],
    };
    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schema);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const handleAnalyze = () => {
    const results = analyzeBusinessNeeds(bizType, bizNeeds, revenue, employees);
    setAnalyzerResults(results);
  };

  const priorityColors = {
    critical: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-500 text-white', label: 'Critical' },
    recommended: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-500 text-white', label: 'Recommended' },
    optional: { bg: 'bg-gray-50', border: 'border-gray-200', badge: 'bg-gray-400 text-white', label: 'Optional' },
  };

  const fmtRevenue = (n: number) => n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : `$${(n / 1000).toFixed(0)}K`;

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Business Life Insurance Indianapolis</span>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/e05ab70d11e1825ca937c89e7d6b19c4.jpg"
            alt="Business life insurance for Indianapolis Indiana business owners"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/95 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-briefcase-line text-sm"></i>
              Indianapolis Business Owners · Key Person · Buy-Sell · SBA Loans
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Business Life Insurance for<br />
              <span className="text-[#D97642]">Indianapolis Companies &amp; Owners</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              Key person insurance, buy-sell agreement funding, SBA loan collateral, and executive benefits for Indianapolis businesses. Use our <strong className="text-white">Business Coverage Needs Analyzer</strong> to find exactly what your business needs.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '6', label: 'Business coverage types' },
                { value: '20+', label: 'Carriers compared' },
                { value: '48 hrs', label: 'Typical approval' },
                { value: '$0', label: 'Cost to compare' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="#analyzer" className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-search-line text-lg"></i>
                Analyze My Business Needs
              </a>
              <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: 'Key Person', label: 'Protects business revenue if a critical person dies' },
              { value: 'Buy-Sell', label: 'Funds ownership transition between partners' },
              { value: 'SBA Loans', label: 'Satisfies lender collateral requirements' },
              { value: 'Group Life', label: 'Employee benefit — tax-deductible for the business' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-xl md:text-2xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content + Sidebar */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">

          {/* LEFT */}
          <div>

            {/* Business Coverage Needs Analyzer */}
            <div id="analyzer" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Interactive Tool</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Business Coverage Needs Analyzer
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Tell us about your Indianapolis business and we'll identify exactly which types of business life insurance you need, the recommended coverage amounts, and estimated monthly costs.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-briefcase-line text-[#D97642]"></i>
                    Your Business Profile
                  </h3>

                  {/* Business Type */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Business Structure</label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { value: 'sole-proprietor', label: 'Sole Proprietor' },
                        { value: 'partnership', label: 'Partnership' },
                        { value: 'llc', label: 'LLC' },
                        { value: 'corporation', label: 'Corporation' },
                        { value: 'professional', label: 'Professional Practice' },
                      ].map(opt => (
                        <button key={opt.value} onClick={() => { setBizType(opt.value as BusinessType); setAnalyzerResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${bizType === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Coverage Needs */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Coverage Needs (select all that apply)</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {[
                        { value: 'key-person', label: 'Key Person Insurance', desc: 'Protect against loss of a critical person' },
                        { value: 'buy-sell', label: 'Buy-Sell Agreement', desc: 'Fund ownership transition between partners' },
                        { value: 'sba-loan', label: 'SBA Loan Collateral', desc: 'Satisfy lender requirements' },
                        { value: 'executive-benefit', label: 'Executive Benefits', desc: 'Attract and retain key executives' },
                        { value: 'group-life', label: 'Group Life Insurance', desc: 'Employee benefit for your team' },
                      ].map(opt => (
                        <button key={opt.value} onClick={() => toggleNeed(opt.value as BusinessNeed)}
                          className={`px-4 py-3 rounded-lg text-left transition-all cursor-pointer ${bizNeeds.includes(opt.value as BusinessNeed) ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          <div className="text-sm font-bold">{opt.label}</div>
                          <div className={`text-xs mt-0.5 ${bizNeeds.includes(opt.value as BusinessNeed) ? 'text-white/80' : 'text-white/50'}`}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Revenue */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-2 block">
                      Annual Business Revenue: <span className="text-[#D97642] font-bold">{fmtRevenue(revenue)}</span>
                    </label>
                    <input type="range" min={100000} max={10000000} step={100000} value={revenue}
                      onChange={e => { setRevenue(Number(e.target.value)); setAnalyzerResults(null); }}
                      className="w-full accent-[#D97642]" />
                    <div className="flex justify-between text-xs text-white/40 mt-1"><span>$100K</span><span>$10M</span></div>
                  </div>

                  {/* Employees */}
                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-2 block">
                      Number of Employees: <span className="text-[#D97642] font-bold">{employees}</span>
                    </label>
                    <input type="range" min={1} max={100} step={1} value={employees}
                      onChange={e => { setEmployees(Number(e.target.value)); setAnalyzerResults(null); }}
                      className="w-full accent-[#D97642]" />
                    <div className="flex justify-between text-xs text-white/40 mt-1"><span>1</span><span>100+</span></div>
                  </div>

                  <button onClick={handleAnalyze}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base">
                    <i className="ri-search-line text-lg"></i>
                    Analyze My Business Coverage Needs
                  </button>
                </div>

                {/* Results */}
                {analyzerResults !== null && (
                  <div className="px-7 py-7">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-base font-bold text-[#1A3A52]">Your Business Coverage Plan — Indianapolis</h3>
                      <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#D97642]/10 text-[#D97642] whitespace-nowrap">
                        {analyzerResults.length} coverage {analyzerResults.length === 1 ? 'type' : 'types'} identified
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mb-6">Based on your business profile. Actual coverage amounts determined after full analysis.</p>

                    {analyzerResults.length === 0 ? (
                      <div className="p-5 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                        Select at least one coverage need above to see your business coverage plan.
                      </div>
                    ) : (
                      <div className="flex flex-col gap-4">
                        {analyzerResults.map((result, i) => {
                          const colors = priorityColors[result.priority];
                          return (
                            <div key={i} className={`rounded-xl border-2 p-5 ${colors.bg} ${colors.border}`}>
                              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                                <div>
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="text-sm font-bold text-[#1A3A52]">{result.need}</span>
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${colors.badge}`}>{colors.label}</span>
                                  </div>
                                  <p className="text-xs text-gray-500">{result.product} · {result.carrier}</p>
                                </div>
                                <div className="text-right">
                                  <div className="text-lg font-extrabold text-[#D97642]">{result.monthlyEst}</div>
                                  <div className="text-xs text-gray-500">estimated</div>
                                </div>
                              </div>
                              <p className="text-sm text-gray-600 leading-relaxed mb-3">{result.description}</p>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <i className="ri-shield-check-line text-[#D97642]"></i>
                                <span>Recommended coverage: <strong className="text-[#1A3A52]">{result.coverageRange}</strong></span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {analyzerResults.length > 0 && (
                      <div className="mt-5 p-4 bg-[#1A3A52] rounded-xl text-center">
                        <p className="text-white text-sm mb-3">Get your exact business coverage quote — free, no obligation.</p>
                        <button onClick={() => setShowBookingModal(true)}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all whitespace-nowrap cursor-pointer">
                          <i className="ri-calendar-check-fill"></i>
                          Book My Free Business Coverage Analysis
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {analyzerResults === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-briefcase-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your business structure, coverage needs, revenue, and employee count above, then click <strong className="text-[#1A3A52]">Analyze My Business Coverage Needs</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* 6 Business Solutions */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Coverage Types</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                6 Types of Business Life Insurance for Indianapolis Companies
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {BUSINESS_SOLUTIONS.map((sol, i) => (
                  <div key={i} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#D97642]/10 flex-shrink-0">
                        <i className={`${sol.icon} text-xl text-[#D97642]`}></i>
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-[#1A3A52]">{sol.title}</h3>
                        <p className="text-xs text-[#D97642] font-semibold">{sol.subtitle}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4">{sol.desc}</p>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      <div className="bg-[#F9F7F4] rounded-lg p-2.5">
                        <div className="text-xs text-gray-400 mb-0.5">Coverage Range</div>
                        <div className="text-xs font-bold text-[#1A3A52]">{sol.coverage}</div>
                      </div>
                      <div className="bg-[#F9F7F4] rounded-lg p-2.5">
                        <div className="text-xs text-gray-400 mb-0.5">Priority</div>
                        <div className={`text-xs font-bold ${sol.priority === 'critical' ? 'text-red-500' : sol.priority === 'recommended' ? 'text-amber-500' : 'text-gray-500'}`}>
                          {sol.priority.charAt(0).toUpperCase() + sol.priority.slice(1)}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {sol.carriers.map(c => (
                        <span key={c} className="text-xs bg-[#1A3A52]/5 text-[#1A3A52] border border-[#1A3A52]/10 px-2 py-0.5 rounded-full font-medium">{c}</span>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400"><strong>Best for:</strong> {sol.bestFor}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Businesses We've Protected
              </h2>
              <div className="grid sm:grid-cols-3 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <span className="inline-block px-3 py-1 bg-[#D97642]/10 text-[#D97642] text-xs font-bold rounded-full mb-3">{t.type}</span>
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>)}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="border-t border-gray-100 pt-3">
                      <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                      <p className="text-xs text-gray-400">{t.business}</p>
                      <p className="text-xs font-bold text-[#D97642] mt-1">{t.coverage}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Business Life Insurance Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug">{faq.q}</h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT SIDEBAR */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-briefcase-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Free Business Analysis</h3>
                    <p className="text-xs text-white/70">Key person · Buy-sell · SBA · Group life</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ 20+ carriers compared</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ No obligation</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Same-day response</span>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Business Consultation
                  </button>
                  <a href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold">
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · Indianapolis business specialists</p>
              </div>
            </div>

            {/* Business Coverage Quick Reference */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-file-list-3-line text-[#D97642]"></i>
                Business Coverage Quick Reference
              </h3>
              <div className="flex flex-col gap-3">
                {[
                  { label: 'Key Person Coverage', rule: '2–5× key person\'s annual compensation' },
                  { label: 'Buy-Sell Funding', rule: 'Equal to each partner\'s ownership value' },
                  { label: 'SBA Loan Collateral', rule: 'Equal to outstanding loan balance' },
                  { label: 'Group Life (per employee)', rule: '$50K–$100K per employee' },
                  { label: 'Executive Benefit (IUL)', rule: '$500K–$5M per executive' },
                ].map((item, i) => (
                  <div key={i} className="flex flex-col py-2 border-b border-gray-100 last:border-0">
                    <p className="text-xs font-bold text-[#1A3A52]">{item.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.rule}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free Business Coverage Call</h3>
                    <p className="text-xs text-white/70">15 minutes · No pressure · Indianapolis</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Business Coverage Analysis',
                    step0Title: 'Pick a Date',
                    step1Title: 'Choose a Time',
                    step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date',
                    selectTime: 'See Available Times →',
                    reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev',
                    nextMonth: 'Next',
                    noSlotsAvailable: 'No slots available for this date',
                    selectedDateTime: 'Your Appointment',
                    modifyTime: 'Change',
                    customerName: 'Full Name',
                    customerPhone: 'Phone Number',
                    notes: 'Business name & coverage need (Optional)',
                    submitButton: 'Confirm My Free Business Coverage Call',
                    submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.',
                    backButton: '← Back to Calendar',
                    required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            {/* Related Links */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4">Related Pages</h3>
              <div className="flex flex-col gap-2">
                {[
                  { to: '/life-insurance-quotes-indianapolis', label: 'Compare All Carriers — Indianapolis', icon: 'ri-search-line' },
                  { to: '/term-life-insurance-indianapolis', label: 'Term Life Insurance Indianapolis', icon: 'ri-calendar-check-line' },
                  { to: '/indexed-universal-life', label: 'Indexed Universal Life (IUL)', icon: 'ri-line-chart-line' },
                  { to: '/wealth-building', label: 'Wealth Building Strategies', icon: 'ri-plant-line' },
                ].map(link => (
                  <Link key={link.to} to={link.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9F7F4] transition-colors group">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-[#1A3A52] font-medium">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Protect Your Indianapolis Business Today
          </h2>
          <p className="text-base text-white/75 mb-7">
            Key person, buy-sell, SBA loans, group life — we compare 20+ carriers to find the right coverage for your business. Free analysis, no obligation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#analyzer" className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-lg"></i>
              Analyze My Business Needs
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={127} productName="Business Life Insurance Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Business Life Insurance" quoteHref="/instant-quote" />

      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Free Business Life Insurance Analysis — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance"
                style={{ width: '100%', height: '600px', border: 'none' }} title="Book Appointment" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
